import { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Checkbox } from '../../components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { CheckCircle, XCircle, Edit2, RotateCcw, AlertCircle, Loader2, Banknote, ShieldAlert, ArrowUp, ArrowDown } from 'lucide-react';
import { toast } from 'sonner';
import SessionDetailModal from '../SessionDetailModal';

export default function AdjustmentsPanel({
  currentRole,
  onPendingCountChange,
  onMutate,
  // V14.5 Part 19z12 — Founder bug (2026-06-01): Review Sessions tab was
  // ignoring the parent's instructor + date filters. Accept them as
  // props so the same window applies across all three views (KPI
  // cards / Instructor Payouts / Review Sessions). Defaults preserve
  // legacy behaviour for any other call sites.
  instructorFilter = 'all',
  filterMode = 'month',
  month,
  dateFrom,
  dateTo,
}) {
  const isSuperadmin = currentRole === 'superadmin';
  const canApprove = currentRole === 'superadmin' || currentRole === 'admin';
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [rejectDialog, setRejectDialog] = useState({ open: false, row: null, reason: '' });
  const [editDialog, setEditDialog] = useState({ open: false, row: null, amount: '', note: '' });
  const [approveDialog, setApproveDialog] = useState({ open: false, row: null, override: '', note: '' });
  const [sessionModal, setSessionModal] = useState({ open: false, session: null });
  const [busy, setBusy] = useState(false);
  // V14.5 Part 19z15 — Founder ask (2026-06-02): date-sort toggle.
  // Default newest-first ("desc"); admin clicks the arrow to flip.
  const [dateSortDir, setDateSortDir] = useState('desc');
  // V14.5 Part 19z15 — Founder ask (2026-06-02): multi-select +
  // bulk-approve. We track selected adjustment ids (set) and a bulk
  // dialog confirm gate.
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkConfirm, setBulkConfirm] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (typeFilter !== 'all') params.type = typeFilter;
      // V14.5 Part 19z12 — propagate the parent's filters
      if (instructorFilter && instructorFilter !== 'all') params.instructor_id = instructorFilter;
      if (filterMode === 'custom') {
        if (dateFrom) params.date_from = dateFrom;
        if (dateTo) params.date_to = dateTo;
      } else if (month) {
        params.month = month;
      }
      const res = await api.get('/api/finance/adjustments', { params });
      const adjustments = res.data.adjustments || [];
      setRows(adjustments);
      // V14.7 — Always report total row count for the tab badge so it
      // matches the KPI "Total Sessions" regardless of active filter.
      if (onPendingCountChange) {
        onPendingCountChange(adjustments.length);
      }
    } catch (err) {
      console.error(err);
      toast.error('Failed to load adjustments');
    } finally { setLoading(false); }
  }, [statusFilter, typeFilter, instructorFilter, filterMode, month, dateFrom, dateTo, onPendingCountChange]);

  useEffect(() => { load(); }, [load]);

  // ─── Mutations ──────────────────────────────────────────────────────────
  const approve = async (row, override = null, note = '') => {
    setBusy(true);
    try {
      const body = {};
      if (override !== null && override !== '') body.override_amount = parseFloat(override);
      if (note) body.note = note;
      await api.post(`/api/finance/adjustments/${row.id}/approve`, body);
      toast.success('Approved');
      setApproveDialog({ open: false, row: null, override: '', note: '' });
      load();
      onMutate?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Approve failed');
    } finally { setBusy(false); }
  };

  const reject = async () => {
    if (!rejectDialog.reason.trim()) return toast.error('Reason is required');
    setBusy(true);
    try {
      await api.post(`/api/finance/adjustments/${rejectDialog.row.id}/reject`, { reason: rejectDialog.reason });
      toast.success('Rejected');
      setRejectDialog({ open: false, row: null, reason: '' });
      load();
      onMutate?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Reject failed');
    } finally { setBusy(false); }
  };

  const editAmount = async () => {
    if (!editDialog.amount || !editDialog.note.trim()) return toast.error('Amount and note are required');
    setBusy(true);
    try {
      await api.post(`/api/finance/adjustments/${editDialog.row.id}/edit-amount`, {
        new_amount: parseFloat(editDialog.amount),
        note: editDialog.note,
      });
      toast.success('Amount edited');
      setEditDialog({ open: false, row: null, amount: '', note: '' });
      load();
      onMutate?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Edit failed');
    } finally { setBusy(false); }
  };

  const reverse = async (row) => {
    if (!window.confirm('Reverse this decision back to Pending? Allowed within 30 days only.')) return;
    setBusy(true);
    try {
      await api.post(`/api/finance/adjustments/${row.id}/reverse`);
      toast.success('Reversed to pending');
      load();
      onMutate?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Reverse failed');
    } finally { setBusy(false); }
  };

  // V14.5 Part 19z15 — sort rows by scheduled_date according to admin toggle.
  // String comparison is correct since scheduled_date is `YYYY-MM-DD`.
  const sortedRows = useMemo(() => {
    const arr = [...rows];
    arr.sort((a, b) => {
      const da = a.scheduled_date || '';
      const db = b.scheduled_date || '';
      if (da === db) return 0;
      if (dateSortDir === 'asc') return da < db ? -1 : 1;
      return da < db ? 1 : -1;
    });
    return arr;
  }, [rows, dateSortDir]);

  // V14.5 Part 19z15 — only PENDING rows can be bulk-approved. The
  // "select all" checkbox in the header acts on the currently-visible
  // pending rows only.
  const pendingSelectable = useMemo(() => sortedRows.filter(r => r.status === 'pending'), [sortedRows]);
  const allPendingSelected = pendingSelectable.length > 0 && pendingSelectable.every(r => selectedIds.has(r.id));
  const someSelected = selectedIds.size > 0;

  const toggleRow = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    setSelectedIds(prev => {
      if (allPendingSelected) {
        // Unselect ALL visible pending rows
        const next = new Set(prev);
        pendingSelectable.forEach(r => next.delete(r.id));
        return next;
      }
      // Select ALL visible pending rows (additive to any prior selection)
      const next = new Set(prev);
      pendingSelectable.forEach(r => next.add(r.id));
      return next;
    });
  };

  // Drop selection when filters change so admins don't accidentally
  // approve rows they can't see.
  useEffect(() => { setSelectedIds(new Set()); }, [statusFilter, typeFilter, instructorFilter, filterMode, month, dateFrom, dateTo]);

  const runBulkApprove = async () => {
    const ids = Array.from(selectedIds);
    if (!ids.length) { toast.error('Nothing selected'); return; }
    setBusy(true);
    try {
      const res = await api.post('/api/finance/adjustments/bulk-approve', { adjustment_ids: ids });
      const { total_approved, total_skipped, total_failed } = res.data;
      if (total_failed > 0) {
        toast.error(`${total_approved} approved · ${total_failed} failed`);
      } else if (total_skipped > 0) {
        toast.success(`${total_approved} approved · ${total_skipped} already approved`);
      } else {
        toast.success(`${total_approved} session${total_approved === 1 ? '' : 's'} approved`);
      }
      setSelectedIds(new Set());
      setBulkConfirm(false);
      await load();
      onMutate?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || 'Bulk approve failed');
    }
    setBusy(false);
  };

  // V14.5 Part 19z15 — friendly "07:00 AM" rendering. Falls back to
  // the raw string when malformed so we never blank the cell.
  const fmtTime12h = (hhmm) => {
    if (!hhmm || typeof hhmm !== 'string') return '';
    const m = hhmm.match(/^(\d{1,2}):(\d{2})/);
    if (!m) return hhmm;
    let h = parseInt(m[1], 10);
    const mins = m[2];
    const suffix = h >= 12 ? 'PM' : 'AM';
    h = h % 12 || 12;
    return `${h}:${mins} ${suffix}`;
  };

  // ─── Render helpers ─────────────────────────────────────────────────────
  const TypeBadge = ({ type, amount }) => {
    if (type === 'no_show_penalty' || type === 'instructor_cancel_penalty' || amount < 0) {
      return <Badge className="bg-rose-50 text-rose-700 border-rose-200 gap-1"><ShieldAlert className="h-3 w-3" />Penalty</Badge>;
    }
    return <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 gap-1"><Banknote className="h-3 w-3" />Payout</Badge>;
  };

  const StatusBadge = ({ status }) => ({
    pending: <Badge className="bg-amber-50 text-amber-700 border-amber-200">Pending</Badge>,
    approved: <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200">Approved</Badge>,
    rejected: <Badge className="bg-slate-100 text-slate-600 border-slate-200">Rejected</Badge>,
  }[status] || <Badge>{status}</Badge>);

  const fmtAmount = (a) => {
    const n = Number(a || 0);
    const sign = n < 0 ? '-' : '';
    return `${sign}INR ${Math.abs(n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // ─── Component output ───────────────────────────────────────────────────
  return (
    <Card className="rounded-2xl">
      <CardContent className="pt-5">
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40" data-testid="adj-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-44" data-testid="adj-type-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              <SelectItem value="session_payout">Session payouts</SelectItem>
              <SelectItem value="no_show_penalty">No-show penalties</SelectItem>
            </SelectContent>
          </Select>
          <div className="flex-1" />
          {/* V14.5 Part 19z15 — bulk-approve action. Only visible when
              admin has approve permission AND at least one row is
              selected. Confirmation modal prevents accidental clicks. */}
          {canApprove && someSelected && (
            <Button
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              onClick={() => setBulkConfirm(true)}
              disabled={busy}
              data-testid="adj-bulk-approve-btn"
            >
              <CheckCircle className="h-4 w-4 mr-1" />
              Approve {selectedIds.size} selected
            </Button>
          )}
          <span className="text-xs text-muted-foreground">{rows.length} row{rows.length !== 1 ? 's' : ''}</span>
        </div>

        {loading ? (
          <div className="text-center py-12"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></div>
        ) : rows.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <AlertCircle className="h-10 w-10 mx-auto mb-2 opacity-30" />
            No adjustments match these filters.
          </div>
        ) : (
          <Table data-testid="adjustments-table">
            <TableHeader>
              <TableRow>
                {/* V14.5 Part 19z15 — bulk-select header checkbox.
                    Acts on the currently visible pending rows only —
                    superadmin/admin can pre-select rows before clicking
                    "Approve N selected" in the toolbar above. */}
                {canApprove && (
                  <TableHead className="w-10">
                    <Checkbox
                      checked={allPendingSelected}
                      onCheckedChange={toggleAll}
                      disabled={pendingSelectable.length === 0}
                      aria-label="Select all pending rows on this page"
                      data-testid="adj-select-all"
                    />
                  </TableHead>
                )}
                <TableHead>Type</TableHead>
                {/* V14.5 Part 19z15 — clickable Date header toggles
                    asc ↔ desc. Visual cue: arrow icon next to label. */}
                <TableHead>
                  <button
                    type="button"
                    onClick={() => setDateSortDir(d => d === 'desc' ? 'asc' : 'desc')}
                    className="inline-flex items-center gap-1 font-medium hover:text-foreground"
                    title={`Click to sort ${dateSortDir === 'desc' ? 'oldest' : 'newest'} first`}
                    data-testid="adj-date-sort-btn"
                  >
                    Date
                    {dateSortDir === 'desc'
                      ? <ArrowDown className="h-3 w-3" />
                      : <ArrowUp className="h-3 w-3" />}
                  </button>
                </TableHead>
                <TableHead>Instructor</TableHead>
                <TableHead>Batch / Session</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedRows.map(r => {
                const effective = r.amount_override !== null && r.amount_override !== undefined ? r.amount_override : r.amount;
                // V14.5 Part 19z15 — Founder ask (2026-06-02): replace
                // the generic "Live Session" subtitle with the actual
                // scheduled time + duration of the conducted session.
                // Falls back gracefully when fields are absent.
                const timeLabel = fmtTime12h(r.session_time);
                const conducted = r.conducted_minutes
                  ?? r.session_duration
                  ?? r.duration_minutes;
                const durLabel = conducted ? `${Math.round(conducted)} min` : '';
                const sessionSubtitle = [timeLabel, durLabel].filter(Boolean).join(' · ');
                return (
                  <TableRow key={r.id} data-testid={`adj-row-${r.id}`}>
                    {canApprove && (
                      <TableCell>
                        <Checkbox
                          checked={selectedIds.has(r.id)}
                          disabled={r.status !== 'pending'}
                          onCheckedChange={() => toggleRow(r.id)}
                          aria-label="Select for bulk approve"
                          data-testid={`adj-select-${r.id}`}
                        />
                      </TableCell>
                    )}
                    <TableCell><TypeBadge type={r.type} amount={effective} /></TableCell>
                    <TableCell className="text-sm">{r.scheduled_date || '—'}</TableCell>
                    <TableCell className="text-sm font-medium">{r.instructor_name || 'Unknown'}</TableCell>
                    <TableCell className="text-sm">
                      {/* V14.5 Part 17j — clickable when session_id is known.
                          Manual / unattributed adjustments stay non-clickable. */}
                      {r.session_id ? (
                        <button
                          type="button"
                          onClick={() => setSessionModal({ open: true, session: { id: r.session_id, scheduled_date: r.scheduled_date, scheduled_time: r.session_time, duration_minutes: r.session_duration, title: r.session_title, batch_id: r.batch_id, batch_name: r.batch_name, batch_type: r.batch_type } })}
                          className="text-left underline-offset-2 hover:underline focus:underline focus:outline-none cursor-pointer"
                          data-testid={`adj-open-session-${r.id}`}
                        >
                          {r.batch_name || '—'}
                          {sessionSubtitle && <span className="block text-xs text-muted-foreground">{sessionSubtitle}</span>}
                        </button>
                      ) : (
                        <>
                          {r.batch_name || '—'}
                          {sessionSubtitle && <p className="text-xs text-muted-foreground">{sessionSubtitle}</p>}
                        </>
                      )}
                    </TableCell>
                    <TableCell className="text-right text-sm font-semibold">
                      {fmtAmount(effective)}
                      {r.amount_override !== null && r.amount_override !== undefined && (
                        <p className="text-[10px] text-muted-foreground line-through">{fmtAmount(r.amount)}</p>
                      )}
                    </TableCell>
                    <TableCell><StatusBadge status={r.status} /></TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        {r.status === 'pending' ? (
                          <>
                            <Button size="sm" variant="outline" className="text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                                    onClick={() => setApproveDialog({ open: true, row: r, override: '', note: '' })}
                                    data-testid={`adj-approve-${r.id}`}>
                              <CheckCircle className="h-3.5 w-3.5 mr-1" />Approve
                            </Button>
                            <Button size="sm" variant="outline" className="text-rose-700 border-rose-200 hover:bg-rose-50"
                                    onClick={() => setRejectDialog({ open: true, row: r, reason: '' })}
                                    data-testid={`adj-reject-${r.id}`}>
                              <XCircle className="h-3.5 w-3.5 mr-1" />Reject
                            </Button>
                            {canApprove && (
                              <Button size="sm" variant="ghost" onClick={() => setEditDialog({ open: true, row: r, amount: String(Math.abs(effective)), note: '' })}
                                      data-testid={`adj-edit-${r.id}`} title="Edit payout amount before approval">
                                <Edit2 className="h-3.5 w-3.5 mr-1" />Edit ₹
                              </Button>
                            )}
                          </>
                        ) : (
                          <>
                            <Button size="sm" variant="ghost" onClick={() => reverse(r)} data-testid={`adj-reverse-${r.id}`}
                                    title="Reverse to pending (within 30 days)">
                              <RotateCcw className="h-3.5 w-3.5 mr-1" />Reverse
                            </Button>
                            {isSuperadmin && (
                              <Button size="sm" variant="ghost" onClick={() => setEditDialog({ open: true, row: r, amount: String(Math.abs(effective)), note: '' })}
                                      data-testid={`adj-edit-${r.id}`} title="Superadmin: edit amount post-decision">
                                <Edit2 className="h-3.5 w-3.5 mr-1" />Edit ₹
                              </Button>
                            )}
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}

        {/* V14.5 Part 19z15 — Bulk-approve confirmation dialog */}
        <Dialog open={bulkConfirm} onOpenChange={(o) => !o && setBulkConfirm(false)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Approve {selectedIds.size} session{selectedIds.size === 1 ? '' : 's'}?</DialogTitle>
              <DialogDescription>
                Each row is approved at its current amount (no overrides). Already-approved rows are skipped.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setBulkConfirm(false)} disabled={busy} data-testid="adj-bulk-confirm-cancel">
                Cancel
              </Button>
              <Button
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                onClick={runBulkApprove}
                disabled={busy}
                data-testid="adj-bulk-confirm-approve"
              >
                {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <CheckCircle className="h-4 w-4 mr-1" />}
                Confirm Approve
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ─── Approve dialog (with optional override) ─── */}
        <Dialog open={approveDialog.open} onOpenChange={(o) => !o && setApproveDialog({ open: false, row: null, override: '', note: '' })}>          <DialogContent>
            <DialogHeader>
              <DialogTitle>Approve adjustment</DialogTitle>
              <DialogDescription>
                Default amount: <strong>{fmtAmount(approveDialog.row?.amount || 0)}</strong>. Override is optional — leave blank to use the system-calculated rate.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <div>
                <label className="text-xs uppercase tracking-wide text-muted-foreground">Override amount (optional, INR)</label>
                <Input type="number" placeholder="e.g. 350" value={approveDialog.override}
                       onChange={(e) => setApproveDialog({ ...approveDialog, override: e.target.value })}
                       data-testid="adj-approve-override" />
              </div>
              {approveDialog.override && (
                <div>
                  <label className="text-xs uppercase tracking-wide text-muted-foreground">Reason for override</label>
                  <Textarea rows={2} placeholder="e.g. Session ran 30 min instead of full 60"
                            value={approveDialog.note}
                            onChange={(e) => setApproveDialog({ ...approveDialog, note: e.target.value })} />
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setApproveDialog({ open: false, row: null, override: '', note: '' })}>Cancel</Button>
              <Button onClick={() => approve(approveDialog.row, approveDialog.override, approveDialog.note)} disabled={busy}
                      data-testid="adj-approve-confirm">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Approve'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ─── Reject dialog ─── */}
        <Dialog open={rejectDialog.open} onOpenChange={(o) => !o && setRejectDialog({ open: false, row: null, reason: '' })}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Reject adjustment</DialogTitle>
              <DialogDescription>
                Tell the instructor why their payout is being declined. This goes on the audit log and is visible on their invoice if it&apos;s a penalty waiver.
              </DialogDescription>
            </DialogHeader>
            <Textarea rows={3} value={rejectDialog.reason}
                      placeholder="e.g. Session was a duplicate accidental click; already paid under session #842"
                      onChange={(e) => setRejectDialog({ ...rejectDialog, reason: e.target.value })}
                      data-testid="adj-reject-reason" />
            <DialogFooter>
              <Button variant="ghost" onClick={() => setRejectDialog({ open: false, row: null, reason: '' })}>Cancel</Button>
              <Button variant="destructive" onClick={reject} disabled={busy} data-testid="adj-reject-confirm">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Reject'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ─── Edit-after-decision dialog (superadmin only) ─── */}
        <Dialog open={editDialog.open} onOpenChange={(o) => !o && setEditDialog({ open: false, row: null, amount: '', note: '' })}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editDialog.row?.status === 'pending' ? 'Edit payout amount' : 'Edit amount (post-decision)'}</DialogTitle>
              <DialogDescription>
                {editDialog.row?.status === 'pending'
                  ? 'Correct this payout before approval — the row stays pending and the new amount is protected from rate-card refreshes.'
                  : `Superadmin override on an already ${editDialog.row?.status} row. Sign is preserved automatically (penalties stay negative).`}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <div>
                <label className="text-xs uppercase tracking-wide text-muted-foreground">New amount (INR)</label>
                <Input type="number" value={editDialog.amount}
                       onChange={(e) => setEditDialog({ ...editDialog, amount: e.target.value })}
                       data-testid="adj-edit-amount-input" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wide text-muted-foreground">Note (required)</label>
                <Textarea rows={2} value={editDialog.note}
                          placeholder="e.g. Quarterly correction agreed with instructor on call"
                          onChange={(e) => setEditDialog({ ...editDialog, note: e.target.value })}
                          data-testid="adj-edit-note" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setEditDialog({ open: false, row: null, amount: '', note: '' })}>Cancel</Button>
              <Button onClick={editAmount} disabled={busy} data-testid="adj-edit-confirm">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>

      {/* V14.5 Part 17j — Unified session-detail modal launched by row click. */}
      <SessionDetailModal
        session={sessionModal.session}
        open={sessionModal.open}
        onClose={() => setSessionModal({ open: false, session: null })}
        onUpdate={load}
      />
    </Card>
  );
}
